/**
 * Data Event Listener
 *
 * Central listener that handles data layer events and dispatches to appropriate services.
 * This keeps components decoupled from the data layer implementation.
 */

import {
  DataEvents,
  DataEventName,
  addDataEventListener,
  resolveDataEvent,
  rejectDataEvent,
  RefreshDetail,
  SyncDetail,
  RefreshClientDocumentsDetail,
  RefreshClientNotesDetail,
  SyncCaseNoteDetail,
  SyncDocumentDetail,
  SyncDocumentsDetail,
  PullDocumentDetail,
  dispatchDataEvent,
  EVENT_DEBOUNCED,
  AppEvents,
} from './events';
import { trackSyncActivity, reportLeftBehind, reportLeftBehindContacts, reportLeftBehindNotes } from './sync-activity';
import { isTransientSyncError, OfflineError } from '../lib/sync-errors';
import { AuthExpiredError } from '../lib/fetch-utils';
import { syncDocument, syncMultipleDocuments, isPendingDocument, isSyncBlocked } from './services/document_service';
import { handleRefreshCurrentUser, getCurrentPerson } from './services/user_service';
import { forgetOrganizationCache } from './services/http_cache_service';
import { refreshClients } from './services/client_service';
import { refreshTemplates } from './services/document_template_service';
import { refreshPrograms, refreshSites } from './services/program_site_service';
import { syncOfflineContacts } from './services/contact_service';
import { syncOfflineCaseNotes, retryCaseNote, refreshClientNotes, refreshNotes, refreshNoteOptions } from './services/case_note_service';
import { CaseNote } from './types/case_note';
import { refreshDocuments, refreshClientDocuments, pullDocument } from './services/document_service';
import { syncAnalyticsEvents } from './services/analytics_service';
import { Document } from './types/document';
import db, { isDataReadable } from './db';
import { createLogger, LogLevel } from '../lib/logger';
import { isOnline } from '../lib/connectivity';

const logger = createLogger('DataEvents', LogLevel.INFO);

// Track if listener is initialized to prevent duplicate registration
let isInitialized = false;

// Store cleanup functions for event listeners
const registeredListeners: (() => void)[] = [];

// Track last refresh time for each event type to prevent redundant calls
const lastRefreshTimes = new Map<DataEventName, number>();

/** Debounce for the global SYNC push; short, because the push carries the user's work. */
export const SYNC_DEBOUNCE_SECONDS = 3;

/**
 * Check if enough time has passed since the last refresh of this type
 * @param eventType The type of event to check
 * @param debounceSeconds The minimum interval in seconds between calls
 * @returns true if refresh should proceed, false if debounced
 */
function shouldProceedWithRefresh(eventType: DataEventName, debounceSeconds: number): boolean {
  const now = Date.now();
  const lastTime = lastRefreshTimes.get(eventType);
  const debounceInterval = debounceSeconds * 1000; // Convert to milliseconds

  if (!lastTime) {
    // First time - proceed
    lastRefreshTimes.set(eventType, now);
    return true;
  }

  const timeSinceLastRefresh = now - lastTime;

  if (timeSinceLastRefresh < debounceInterval) {
    logger.debug(`Debounced ${eventType}: ${Math.round(timeSinceLastRefresh / 1000)}s since last refresh (minimum ${debounceSeconds}s)`);
    return false;
  }

  // Enough time has passed - proceed and update timestamp
  lastRefreshTimes.set(eventType, now);
  return true;
}

/**
 * Register an event handler with optional debouncing
 * @param eventName The event to listen for
 * @param handler The async function to execute (can optionally receive event detail and return a value)
 * @param options Configuration options
 * @param options.debounce Minimum interval in seconds between calls (0 or undefined = no debouncing)
 */
function registerEvent<T = undefined, R = void>(
  eventName: DataEventName,
  handler: (detail?: T) => Promise<R>,
  options: { debounce?: number; trackActivity?: boolean } = {}
): void {
  registeredListeners.push(
    addDataEventListener(eventName, async (detail) => {
      // Check debounce if configured. A forced dispatch (manual sync) skips the
      // check but still records the run so the next automatic one is debounced.
      const forced = Boolean((detail as { force?: boolean } | undefined)?.force);
      if (options.debounce) {
        if (forced) {
          lastRefreshTimes.set(eventName, Date.now());
        } else if (!shouldProceedWithRefresh(eventName, options.debounce)) {
          resolveDataEvent(detail.__eventId, EVENT_DEBOUNCED);
          return;
        }
      }

      try {
        const run = () => handler(detail as T);
        // Progress and completion are shown only for a reconnect or a manual
        // request; everything else (dashboard load, startup, unlock, tab
        // mounts) syncs silently. Connectivity failures (offline guard,
        // NetworkError, TimeoutError) are "not now" for automatic syncs and
        // settle back to idle — but a user who asked for a sync always gets
        // an answer, so manual failures are never quiet.
        const request = detail as { force?: boolean; reason?: string } | undefined;
        const visible = forced || request?.reason === 'reconnect';
        const result = options.trackActivity
          ? await trackSyncActivity(run, { quiet: forced ? undefined : isTransientSyncError, visible })
          : await run();
        resolveDataEvent(detail.__eventId, result);
      } catch (error) {
        // A connectivity failure must not hold the debounce window: the
        // 'server-reachable' retry a moment later has to be allowed through.
        // Other failures keep the window so a persistently broken leg is not
        // re-run on every mount and reconnect.
        if (isTransientSyncError(error)) {
          lastRefreshTimes.delete(eventName);
        }
        rejectDataEvent(detail.__eventId, error instanceof Error ? error : new Error('Unknown error'));
      }
    })
  );
}

/**
 * Initialize the data event listener
 * Should be called once at app startup
 */
export function initDataEventListener(): void {
  if (isInitialized) {
    logger.warn('Data event listener already initialized');
    return;
  }

  // Global refresh - fetch fresh data from server, then push pending changes
  registerEvent<RefreshDetail>(DataEvents.REFRESH, handleRefreshAll, { debounce: 15, trackActivity: true });

  // Global sync - push all pending changes. A short debounce collapses the
  // reconnect fan-out (online, server-reachable, unlocked, session-ready can
  // all land within seconds) without holding back a push for long.
  registerEvent<SyncDetail>(DataEvents.SYNC, handleSyncAll, { debounce: SYNC_DEBOUNCE_SECONDS, trackActivity: true });

  // Refresh clients from server
  registerEvent(DataEvents.REFRESH_CLIENTS, handleRefreshClients, { debounce: 15 });

  // Refresh templates from server
  registerEvent(DataEvents.REFRESH_TEMPLATES, handleRefreshTemplates, { debounce: 15 });

  // Refresh documents from server
  registerEvent(DataEvents.REFRESH_DOCUMENTS, handleRefreshDocuments, { debounce: 15 });

  // Refresh documents for a specific client
  registerEvent<RefreshClientDocumentsDetail>(DataEvents.REFRESH_CLIENT_DOCUMENTS, async (detail) => {
    await handleRefreshClientDocuments(detail!);
  });

  // Refresh programs from server
  registerEvent(DataEvents.REFRESH_PROGRAMS, handleRefreshPrograms, { debounce: 15 });

  // Refresh sites from server
  registerEvent(DataEvents.REFRESH_SITES, handleRefreshSites, { debounce: 15 });

  // Refresh notes for a specific client
  registerEvent<RefreshClientNotesDetail>(DataEvents.REFRESH_CLIENT_NOTES, async (detail) => {
    await handleRefreshClientNotes(detail!);
  });

  // Refresh the note form's options
  registerEvent(DataEvents.REFRESH_NOTE_OPTIONS, handleRefreshNoteOptions, { debounce: 15 });

  // Sync all documents
  registerEvent<SyncDocumentsDetail>(DataEvents.SYNC_DOCUMENTS, handleSyncDocuments, { trackActivity: true });

  // Sync a specific document
  registerEvent<SyncDocumentDetail, Document | null>(DataEvents.SYNC_DOCUMENT, async (detail) => {
    return await handleSyncSingleDocument(detail!);
  });

  // Pull a specific document from server
  registerEvent<PullDocumentDetail, Document | null>(DataEvents.PULL_DOCUMENT, async (detail) => {
    return await handlePullDocument(detail!);
  });

  // Sync analytics events
  registerEvent(DataEvents.SYNC_ANALYTICS, handleSyncAnalytics, { debounce: 10 });

  // Upload notes written on this device
  registerEvent(DataEvents.SYNC_CASE_NOTES, handleSyncCaseNotes, { trackActivity: true });

  // Upload one note again (Try again)
  registerEvent<SyncCaseNoteDetail, CaseNote | null>(DataEvents.SYNC_CASE_NOTE, async (detail) => {
    return await handleSyncSingleCaseNote(detail!);
  });

  // Do a full refresh (pull server changes, then push pending offline edits)
  // whenever connectivity returns, regardless of which screen is mounted.
  // Component-level reconnect listeners (e.g. the client DocumentsTab) only
  // cover their own mounted lifetime, and the dashboard used to be the only
  // screen that pulled — users learned to visit the homepage to "make it
  // sync". Reconnecting must be enough on its own. The same refresh runs when
  // the app becomes able to sync for other reasons: the encrypted database
  // was just unlocked (it is closed, and every read fails, while locked) or a
  // signed-in session became available at startup on a non-dashboard route.
  const refreshTriggers = ['online', 'server-reachable', AppEvents.UNLOCKED, AppEvents.SESSION_READY];
  refreshTriggers.forEach((name) => window.addEventListener(name, handleConnectivityRestored));
  registeredListeners.push(() => {
    refreshTriggers.forEach((name) => window.removeEventListener(name, handleConnectivityRestored));
  });

  isInitialized = true;
  logger.info('Data event listener initialized');
}

/**
 * Pull server changes and push pending local edits when the app becomes able
 * to sync: connectivity returned ('online' / 'server-reachable'), the encrypted
 * database was unlocked, or a session became available at startup. Dispatched
 * through the REFRESH event so it shares the debounce with the dashboard's own
 * refresh and shows up in the sync activity tracker.
 *
 * After a plain offline→online cycle src/lib/connectivity still considers the
 * server reachable, so the refresh normally runs right on 'online'. Only when
 * a request had actually failed (serverReachable false) is 'online' skipped;
 * the 'server-reachable' event that follows the successful probe covers it.
 */
function handleConnectivityRestored(event: Event): void {
  // Nothing to do without a verified server. While the app is locked the
  // encrypted database is closed and every read would fail; LockContext fires
  // AppEvents.UNLOCKED once it is open again and this handler runs then. The
  // lock screen also opens the database WITHOUT the key to read credentials
  // (ensureDatabaseOpen); a refresh in that window would read every encrypted
  // table as ciphertext and, for httpCache, drop manifest-less rows, so an
  // open database is not enough — see isDataReadable.
  if (!isOnline() || !isDataReadable()) return;
  // Only a real reconnect is shown to the user as progress/completion; the
  // unlock and startup refreshes run silently (failures still surface).
  const reason = event.type === 'online' || event.type === 'server-reachable' ? 'reconnect' : undefined;
  dispatchDataEvent(DataEvents.REFRESH, { reason })
    .then((result) => {
      // The refresh is debounced (15s). Losing the pull to that is fine, but
      // the push carries the user's unsaved work — a reconnect right after a
      // refresh must still push, so fall back to the short-debounced SYNC.
      if (result === EVENT_DEBOUNCED) return dispatchDataEvent(DataEvents.SYNC, { reason });
    })
    .catch((error) => {
      logger.debug('Reconnect refresh skipped', error);
    });
}

/**
 * Test-only: forget every debounce timestamp so the next dispatch of each
 * event runs. Debounce state lives in module scope and would otherwise leak
 * between tests that share one listener registration.
 */
export function resetDebounceForTests(): void {
  lastRefreshTimes.clear();
}

/**
 * Cleanup the data event listener
 * Useful for testing or app teardown
 */
export function cleanupDataEventListener(): void {
  registeredListeners.forEach((cleanup) => cleanup());
  registeredListeners.length = 0;
  lastRefreshTimes.clear();
  isInitialized = false;
  logger.info('Data event listener cleaned up');
}

// A forced refresh bypasses the debounce and the UI guards read React state
// that can lag a tick, so two refreshes can be asked for in the same instant
// (sidebar + Settings, or a double tap). Share one run instead of pulling
// twice; the push legs already coalesce.
let refreshAllInFlight: Promise<void> | null = null;

/**
 * Handle refreshing all data from server
 */
function handleRefreshAll(detail?: RefreshDetail): Promise<void> {
  if (!isOnline()) {
    return Promise.reject(new OfflineError('Cannot refresh while offline'));
  }
  // A forced refresh landing during an automatic one would be coalesced into
  // it unforced; Settings disables "Sync now" while a sync is in flight, so
  // that cannot happen from the one caller that forces.
  if (refreshAllInFlight) return refreshAllInFlight;
  refreshAllInFlight = runRefreshAll(detail).finally(() => {
    refreshAllInFlight = null;
  });
  return refreshAllInFlight;
}

async function runRefreshAll(detail?: RefreshDetail): Promise<void> {
  // Refresh in order: user/programs/sites first, then clients (needed for documents), then templates, then documents
  let pullError: unknown = null;
  try {
    // Settings > "Sync now": no stored validator may answer for a page this
    // time. Forgetting is best-effort (the service logs and swallows), and a
    // validator that survives only costs a 304 the walk still verifies.
    if (detail?.ignoreCache) {
      const currentPerson = await getCurrentPerson();
      if (currentPerson) await forgetOrganizationCache(currentPerson.organization.id);
    }
    await Promise.all([
      handleRefreshCurrentUser(),
      handleRefreshPrograms(),
      handleRefreshSites(),
      handleRefreshNoteOptions(),
    ]);
    await handleRefreshClients();
    await handleRefreshTemplates();
    await handleRefreshDocuments();
    await handleRefreshNotes();
  } catch (error) {
    // An expired session has already cleared the local credentials (see
    // fetchWithAuth); every push request would fail the same way and re-fire
    // the auth-expired handling, so stop here.
    if (error instanceof AuthExpiredError) throw error;
    // Pushing local work is the more important half and must not depend on
    // the pull: a failing template or document fetch would otherwise strand
    // offline edits on the device. Remember the error and rethrow after.
    pullError = error;
    logger.warn('Pull failed; pushing pending changes anyway', error);
  }

  try {
    await handleSyncAll(detail);
  } catch (pushError) {
    // A real pull failure (e.g. a 500) is more informative than a transient
    // push failure (connectivity dropped mid-refresh): report the real one so
    // it is not hidden behind a quiet retry.
    if (pullError && !isTransientSyncError(pullError)) {
      logger.warn('Push also failed after the pull failed', pushError);
      throw pullError;
    }
    if (pullError) logger.warn('Pull had also failed before the push failed', pullError);
    throw pushError;
  }

  if (pullError) throw pullError;
}

/**
 * Handle syncing all pending data
 */
async function handleSyncAll(detail?: RefreshDetail | SyncDetail): Promise<void> {
  if (!isOnline()) {
    throw new OfflineError('Cannot sync while offline');
  }
  // Settings > "Sync now" dispatches REFRESH with force; that has to reach the
  // document sweep or the button ignores the held documents listed above it.
  const force = detail !== undefined && 'force' in detail ? detail.force : undefined;

  // First sync contacts since documents may reference them
  await handleSyncContacts();

  // Sync documents, notes and analytics in parallel
  await Promise.all([
    handleSyncDocuments(force ? { force: true } : undefined),
    handleSyncCaseNotes(),
    handleSyncAnalytics(),
  ]);
}

/**
 * Handle refreshing clients from server
 */
async function handleRefreshClients(): Promise<void> {
  if (!isOnline()) {
    throw new OfflineError('Cannot refresh clients while offline');
  }

  await refreshClients();
  logger.debug('Clients refresh completed');
}

/**
 * Handle refreshing templates from server
 */
async function handleRefreshTemplates(): Promise<void> {
  if (!isOnline()) {
    throw new OfflineError('Cannot refresh templates while offline');
  }

  await refreshTemplates();
  logger.debug('Templates refresh completed');
}

/**
 * Handle refreshing documents from server
 */
async function handleRefreshDocuments(): Promise<void> {
  if (!isOnline()) {
    throw new OfflineError('Cannot refresh documents while offline');
  }

  await refreshDocuments();
  logger.debug('Documents refresh completed');
}

/**
 * Handle refreshing documents for a specific client
 */
async function handleRefreshClientDocuments(detail: RefreshClientDocumentsDetail): Promise<void> {
  if (!detail?.clientId) {
    throw new Error('No client ID provided for refresh');
  }

  if (!isOnline()) {
    throw new OfflineError('Cannot refresh documents while offline');
  }

  await refreshClientDocuments(detail.clientId);
  logger.debug(`Client ${detail.clientId} documents refresh completed`);
}

/**
 * Handle refreshing programs from server
 */
async function handleRefreshPrograms(): Promise<void> {
  if (!isOnline()) {
    throw new OfflineError('Cannot refresh programs while offline');
  }

  await refreshPrograms();
  logger.debug('Programs refresh completed');
}

/**
 * Handle refreshing sites from server
 */
async function handleRefreshSites(): Promise<void> {
  if (!isOnline()) {
    throw new OfflineError('Cannot refresh sites while offline');
  }

  await refreshSites();
  logger.debug('Sites refresh completed');
}

/**
 * Handle refreshing notes for every client
 */
async function handleRefreshNotes(): Promise<void> {
  if (!isOnline()) {
    throw new OfflineError('Cannot refresh notes while offline');
  }

  await refreshNotes();
  logger.debug('Notes refresh completed');
}

/**
 * Handle refreshing notes for a specific client
 */
async function handleRefreshClientNotes(detail: RefreshClientNotesDetail): Promise<void> {
  if (!detail?.clientId) {
    throw new Error('No client ID provided for refresh');
  }

  if (!isOnline()) {
    throw new OfflineError('Cannot refresh notes while offline');
  }

  await refreshClientNotes(detail.clientId);
  logger.debug(`Client ${detail.clientId} notes refresh completed`);
}

/**
 * Handle refreshing the note form's options
 */
async function handleRefreshNoteOptions(): Promise<void> {
  if (!isOnline()) {
    throw new OfflineError('Cannot refresh note options while offline');
  }

  await refreshNoteOptions();
  logger.debug('Note options refresh completed');
}

/**
 * Handle uploading notes written on this device
 */
async function handleSyncCaseNotes(): Promise<void> {
  if (!isOnline()) {
    throw new OfflineError('Cannot sync notes while offline');
  }

  const { synced, failed } = await syncOfflineCaseNotes();
  reportLeftBehindNotes(failed);
  logger.info(`Notes sync completed (${synced} synced, ${failed} failed)`);
}

/**
 * Handle uploading one note again
 */
async function handleSyncSingleCaseNote(detail: SyncCaseNoteDetail): Promise<CaseNote | null> {
  if (!isOnline()) {
    throw new OfflineError('Cannot sync notes while offline');
  }

  return await retryCaseNote(detail.noteId);
}

/**
 * Handle syncing contacts
 */
async function handleSyncContacts(): Promise<void> {
  if (!isOnline()) {
    throw new OfflineError('Cannot sync contacts while offline');
  }

  const { synced, failed } = await syncOfflineContacts();
  reportLeftBehindContacts(failed);
  logger.info(`Contacts sync completed (${synced} synced, ${failed} failed)`);
}

// Coalesces overlapping document-sync sweeps into one. Reconnect fans out to
// several triggers at once (window 'online' + 'server-reachable', the client
// DocumentsTab dispatch, the mount auto-sync), and running them in parallel let
// each sweep read the same unsynced snapshot and re-POST the same draft —
// spawning duplicate documents. While a sweep runs, later triggers just flag a
// re-run so newly-unsynced work still gets picked up on one more pass.
let syncDocumentsInFlight: Promise<void> | null = null;
let syncDocumentsRerunRequested = false;
// A forced dispatch that arrives while a sweep is running would otherwise be
// coalesced into that sweep's unforced pass — its detail is discarded — and the
// caller would get a resolved promise reporting success over documents that
// were never sent. Reconnect fans several triggers out at once, which is
// exactly when a forced one can land mid-sweep.
let syncDocumentsForceRequested = false;

/**
 * Handle syncing all unsynced documents
 */
function handleSyncDocuments(detail?: SyncDocumentsDetail): Promise<void> {
  if (!isOnline()) {
    // Reject rather than throw: the function is typed Promise<void> and no
    // longer async, so a synchronous throw would escape a `.catch()` caller.
    return Promise.reject(new OfflineError('Cannot sync documents while offline'));
  }

  if (syncDocumentsInFlight) {
    syncDocumentsRerunRequested = true;
    if (detail?.force) syncDocumentsForceRequested = true;
    return syncDocumentsInFlight;
  }

  syncDocumentsInFlight = (async () => {
    try {
      const attemptedIds = new Set<number | string>();
      syncDocumentsForceRequested = detail?.force === true;
      do {
        syncDocumentsRerunRequested = false;
        // Consumed, not just read: a force applies to exactly the pass the user
        // asked for. Left set, an unrelated rerun (an autosave landing during
        // the forced pass) would re-send every held payload a second time.
        const force = syncDocumentsForceRequested;
        syncDocumentsForceRequested = false;

        // Get all unsynced documents (documents with synced: false and super_form data),
        // minus any holding on a payload the server already refused — re-sending
        // an unchanged rejected body just reproduces the same error, and did so
        // 18 times over 23 hours before this guard existed. They come back into
        // the sweep the moment the local record changes.
        // `force` is an explicit user gesture — Settings > "Sync now" (via
        // REFRESH) — and means the same thing as the per-row Try again (which
        // goes through SYNC_DOCUMENT): send it even if we're holding it.
        const pending = await db.documents.filter(isPendingDocument).toArray();
        // One predicate call per document, and the batch is derived from the
        // held set rather than re-asking. Recomputed every pass from fresh rows:
        // a document held in pass 1 and fixed by the write that triggered pass 2
        // uploads in pass 2 and must not still be reported as left behind.
        // attemptedIds carries the cross-pass part; this is whatever is held now.
        const blockedIds = new Set<number | string>();
        if (!force) {
          for (const doc of pending) {
            if (doc.id !== undefined && isSyncBlocked(doc)) blockedIds.add(doc.id);
          }
        }
        const unsyncedDocuments = pending.filter(doc => doc.id === undefined || !blockedIds.has(doc.id));

        if (unsyncedDocuments.length === 0) {
          logger.debug('No unsynced documents to sync');
        } else {
          logger.info(`Syncing ${unsyncedDocuments.length} documents`);
          unsyncedDocuments.forEach((doc) => { if (doc.id !== undefined) attemptedIds.add(doc.id); });
          // Only pass options when forcing: selecting a held document for the
          // batch isn't enough, syncDocument holds it again unless told not to.
          await (force
            ? syncMultipleDocuments(unsyncedDocuments, undefined, { force: true })
            : syncMultipleDocuments(unsyncedDocuments));
          logger.info('Documents sync completed');
        }

        // Only documents this sweep attempted and that are still pending count
        // as left behind (sync errors such as a permission problem). A document
        // autosaved while the sweep ran is simply not uploaded yet, not failed.
        // Counted inside the loop so a rerun requested while counting is
        // honoured rather than dropped.
        //
        // Documents we held are added back in. The sweep stopped knocking, but
        // their work is still only on this device — leaving them out reported
        // zero, which the banner renders as "Everything is up to date" while a
        // rejected document sits there. That banner is the only signal an
        // assessor sees without going looking.
        const stillPending = attemptedIds.size === 0
          ? []
          : await db.documents.where('id').anyOf([...attemptedIds]).and(isPendingDocument).primaryKeys();
        const leftBehind = new Set<number | string>([...stillPending as Array<number | string>, ...blockedIds]);
        reportLeftBehind(leftBehind.size);
      } while (syncDocumentsRerunRequested);
    } finally {
      syncDocumentsInFlight = null;
    }
  })();

  return syncDocumentsInFlight;
}

/**
 * Handle syncing a single document
 * Returns the updated document (which may have a new ID after sync)
 */
async function handleSyncSingleDocument(detail: SyncDocumentDetail): Promise<Document | null> {
  if (!detail?.documentId) {
    throw new Error('No document ID provided for sync');
  }

  if (!isOnline()) {
    throw new OfflineError('Cannot sync while offline');
  }

  const document = await db.documents.get(detail.documentId);
  if (!document) {
    throw new Error(`Document not found: ${detail.documentId}`);
  }

  const updatedDocument = await syncDocument(document, undefined, { force: detail.force });
  logger.debug('Single document sync completed');

  return updatedDocument;
}

/**
 * Handle pulling a single document from the server
 * Used when WebSocket notifies us that a document has changed
 * Returns the updated document
 */
async function handlePullDocument(detail: PullDocumentDetail): Promise<Document | null> {
  if (!detail?.clientId || !detail?.documentId) {
    throw new Error('Client ID and document ID are required for pull document');
  }

  if (!isOnline()) {
    throw new OfflineError('Cannot pull document while offline');
  }

  const updatedDocument = await pullDocument(detail.clientId, detail.documentId);
  return updatedDocument;
}

/**
 * Handle syncing analytics events
 */
async function handleSyncAnalytics(): Promise<void> {
  // Analytics can sync silently in the background, so don't throw if offline
  if (!isOnline()) {
    logger.debug('Offline - analytics will sync when connection is restored');
    return;
  }

  const syncedCount = await syncAnalyticsEvents();
  logger.info(`Synced ${syncedCount} analytics events`);
}
